// apps/web/src/components/painel/home/assinatura-cabines.tsx — Fase 17.8, Plano 08 (D-12; UI-SPEC U-17, U-24; T-17.8-08-01).
// Assinatura "Cabines · cor em aplicação" do PCP: uma amostra de 32px por cabine com a cor RAL da sessão aberta. A API manda só o
// texto cadastrado da cor (`corCodigo`); o hex vem do mapa de dados RAL_HEX por `normalizarCor` — o texto livre nunca vira estilo.
// Cabine parada: hachura e o texto "parada". Cor desconhecida: cinza neutro com o código escrito. Nenhuma cabine em aplicação:
// uma linha amigável com PaintBucket.
import { useId, type CSSProperties, type ReactElement } from 'react';
import { PaintBucket } from 'lucide-react';
import type { CabineEmAplicacao } from '@pieltec/contracts';
import { podeAbrirRota } from '../../../lib/painel/blocos';
import { codigoDaCor, nomeDaCor, normalizarCor } from '../../../lib/painel/home/ral-hex';
import { EstadoVazio } from './estado-vazio';
import '../../../styles/painel-home.css';

export const TEXTO_SEM_CABINES = 'Nenhuma cabine em aplicação.';

export interface AssinaturaCabinesProps {
  readonly cabines: readonly CabineEmAplicacao[];
  readonly permissoes: readonly string[];
}

interface Apresentacao {
  readonly texto: string;
  readonly rotuloAcessivel: string;
  readonly classe: string;
  readonly hex?: string;
}

/** Como uma cabine aparece: amostra, código sob ela e frase de acessibilidade. */
export function apresentarCabine(cabine: CabineEmAplicacao): Apresentacao {
  if (!cabine.emAplicacao) {
    return { texto: 'parada', rotuloAcessivel: `Cabine ${cabine.codigo}: parada.`, classe: 'painel-amostra painel-amostra--parada' };
  }
  const cor = normalizarCor(cabine.corCodigo);
  if (cor === null) {
    return { texto: 'sem cor', rotuloAcessivel: `Cabine ${cabine.codigo}: em aplicação, sem cor cadastrada.`, classe: 'painel-amostra painel-amostra--neutra' };
  }
  if (cor.tipo === 'ral') {
    return { texto: codigoDaCor(cor), rotuloAcessivel: `Cabine ${cabine.codigo}: ${nomeDaCor(cor)}, em aplicação.`, classe: 'painel-amostra', hex: cor.hex };
  }
  // PTFE e cor desconhecida: cinza neutro com o código escrito (PTFE não tem um RAL).
  return { texto: codigoDaCor(cor), rotuloAcessivel: `Cabine ${cabine.codigo}: ${nomeDaCor(cor)}, em aplicação.`, classe: 'painel-amostra painel-amostra--neutra' };
}

export function AssinaturaCabines({ cabines, permissoes }: AssinaturaCabinesProps): ReactElement {
  const idTitulo = useId();
  const algumaEmAplicacao = cabines.some((cabine) => cabine.emAplicacao);
  return (
    <section className="painel-assinatura" data-testid="painel-assinatura" aria-labelledby={idTitulo}>
      <header className="painel-assinatura__cabecalho">
        <h3 id={idTitulo} className="painel-assinatura__titulo">
          Cabines
        </h3>
        <span className="painel-assinatura__subtitulo">cor em aplicação</span>
      </header>
      {algumaEmAplicacao ? (
        <ul className="painel-assinatura__grade" style={{ '--n': Math.min(Math.max(cabines.length, 1), 7) } as CSSProperties}>
          {cabines.map((cabine) => {
            const visao = apresentarCabine(cabine);
            const conteudo = (
              <>
                <span className={visao.classe} style={visao.hex === undefined ? undefined : { background: visao.hex }} aria-hidden="true" />
                <span className="painel-cabine__nome">{cabine.codigo}</span>
                <span className="painel-cabine__codigo">{visao.texto}</span>
              </>
            );
            return (
              <li key={cabine.codigo} className="painel-cabine" data-testid={`painel-cabine-${cabine.codigo}`} data-em-aplicacao={cabine.emAplicacao ? 'true' : 'false'}>
                {podeAbrirRota(cabine.href, permissoes) ? (
                  <a className="painel-cabine__link" href={cabine.href} aria-label={visao.rotuloAcessivel}>
                    {conteudo}
                  </a>
                ) : (
                  <div className="painel-cabine__link" role="group" aria-label={visao.rotuloAcessivel}>
                    {conteudo}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      ) : (
        <EstadoVazio icone={PaintBucket} texto={TEXTO_SEM_CABINES} />
      )}
    </section>
  );
}
